import useGetFieldset, {
  CustomFieldDefinitionActionAvailability,
} from '@/api/queries/customField/useGetFieldset';
import meKeys from '@/api/queries/me/keyFactory';
import { useGetCustomFieldGroup } from '@/api/queries/me/useGetMyCustomFieldGroups';
import CreateEditCustomFieldFieldset from '@/ui/components/customFieldGroupControl/components/CreateEditCustomFieldFieldset';
import FieldsetChildrenList from '@/ui/components/customFieldGroupControl/components/FieldsetChildrenList';
import NestedFieldEditorSidebar from '@/ui/components/customFieldGroupControl/components/NestedFieldEditorSidebar';
import {
  useCreateCustomField,
  useUpdateCustomField,
} from '@/ui/components/customFieldGroupControl/hooks';
import { registerFieldLegacyBuilder } from '@/utils/zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  Conditional,
  EmptyState,
  Field,
  Fieldset,
  Form,
  Sidebar,
  ValidationMessages,
} from '@payaca/ambrosia';

import { FieldsetCustomFieldDefinition } from '@payaca/custom-fields';
import { clstx } from '@payaca/utilities/cvaMerge';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import {
  buildDefaultValuesForFieldset,
  buildDynamicValidationSchema,
  FIELDSET_CUSTOM_FIELD_SCHEMA,
  NestedFieldsetFormValue,
} from './schema';
import { PathToFieldsetChild } from './types';

export type NestedFieldEditorState = {
  parentFieldIndex: number;
  nestedFieldIndex: number;
} | null;

export type FormValues = z.infer<typeof FIELDSET_CUSTOM_FIELD_SCHEMA> & {
  selectedFieldset?: string;
};

export type TCreateEditCustomFieldSidebarContentProps = {
  onFieldsetSelectionChange?: (selected: boolean) => void;
  setIsFormDirty?: (isDirty: boolean) => void;
  onSubmit: (data: FormValues, originalData?: FormValues) => void;
  allCurrentIdentifiers: string[];
  fieldset?: FieldsetCustomFieldDefinition;
  isProcessing?: boolean;
  actionAvailability: CustomFieldDefinitionActionAvailability[];
  nestedFieldEditorState: NestedFieldEditorState;
  onOpenNestedFieldEditor: (parentIndex: number, nestedIndex: number) => void;
  onCloseNestedFieldEditor: () => void;
};

const CreateEditCustomFieldSidebarContent: FC<
  TCreateEditCustomFieldSidebarContentProps
> = (props) => {
  const {
    onFieldsetSelectionChange,
    setIsFormDirty,
    onSubmit,
    allCurrentIdentifiers,
    fieldset,
    isProcessing,
    actionAvailability,
    nestedFieldEditorState,
    onOpenNestedFieldEditor,
    onCloseNestedFieldEditor,
  } = props;

  const validationSchema = buildDynamicValidationSchema(
    fieldset,
    actionAvailability
  );

  const defaultValues = fieldset
    ? buildDefaultValuesForFieldset(fieldset)
    : undefined;

  const formMethods = useForm<FormValues>({
    defaultValues: defaultValues || {
      type: 'fieldset',
      identification: {},
    },

    resolver: zodResolver(validationSchema),
  });

  const {
    control,
    watch,
    formState: { errors, isDirty },
  } = formMethods;

  const {
    fields,
    append: appendChildField,
    remove: removeChildField,
    replace: replaceChildFields,
    move: moveChildField,
  } = useFieldArray({
    name: 'children',
    control,
  });

  const registerField = registerFieldLegacyBuilder(errors);

  const type = watch('type');
  const selectedFieldset = watch('selectedFieldset');

  const rightPanelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (rightPanelRef.current) {
      rightPanelRef.current.scrollTop = 0;
    }
  }, [selectedFieldset]);

  const fieldsetIdentifier = fieldset?.identifier;

  const fieldsetChildren = watch('children');
  // Controlled Field Array - see useFieldArray react-hook-form docs
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...fieldsetChildren?.[index],
    };
  });

  const fieldsetChildrenIdentifiers = controlledFields.map(
    (x) => x.identification.identifier
  );

  const fieldsetChildrenValidation = registerField('children').validationState;

  // Auto-select newly added fieldset children
  useEffect(() => {
    if (!fields.length || !isDirty) {
      return;
    }

    formMethods.setValue('selectedFieldset', fields[fields.length - 1].id);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'fields', 'formMethods', and 'isDirty'
  }, [fields.length]);

  useEffect(() => {
    onFieldsetSelectionChange?.(type === 'fieldset');

    // If the type is changed to something other than fieldset, remove all fieldset children
    if (type !== 'fieldset') {
      replaceChildFields([]);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'replaceChildFields', and 'onFieldsetSelectionChange'
  }, [type]);

  useEffect(() => {
    setIsFormDirty?.(isDirty);
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'setIsFormDirty'
  }, [isDirty]);

  const isEditing = !!fieldset;

  return (
    <Form<FormValues>
      methods={formMethods}
      className={clstx(
        'flex-1 flex flex-col min-h-0',
        type === 'fieldset' && 'bg-gray-50'
      )}
      onSubmit={(data) => onSubmit(data, defaultValues)}
      onError={console.error}
    >
      <div className="flex min-h-0 flex-1">
        <div
          className={clstx(
            'overflow-y-auto p-4 flex flex-col bg-white',
            type === 'fieldset' ? 'w-1/2' : 'flex-1'
          )}
        >
          <Fieldset>
            <CreateEditCustomFieldFieldset
              allCurrentIdentifiers={allCurrentIdentifiers}
              isDestructivelyUpdateable={{ result: false, reasons: [] }}
              isPersisted={isEditing}
            />

            <Conditional condition={type === 'fieldset'}>
              <Card
                variant={
                  fieldsetChildrenValidation &&
                  !fieldsetChildrenValidation.isValid
                    ? 'error'
                    : 'default'
                }
              >
                <Card.Body>
                  <Controller
                    render={({ field: { value, onChange, name } }) => {
                      return (
                        <Field name={name}>
                          <div className="flex justify-between mb-4">
                            <Field.Label className="m-0">Fields</Field.Label>

                            <Button
                              className="-mt-2"
                              variant={'white'}
                              size={'sm'}
                              onClick={() => {
                                appendChildField({
                                  type: 'text',
                                  identification: {
                                    label: '',
                                    identifier: '',
                                  },
                                });
                              }}
                            >
                              Add field
                            </Button>
                          </div>

                          <FieldsetChildrenList
                            fields={controlledFields}
                            selectedId={value}
                            onSelect={onChange}
                            onRemove={(index) => {
                              removeChildField(index);
                              onChange(undefined);
                            }}
                            onMove={moveChildField}
                            canRemoveChild={(index) => {
                              const field = controlledFields[index];
                              const aa = actionAvailability.find(
                                (x) =>
                                  x.fieldPath ===
                                  `${fieldsetIdentifier}.${field.identification.identifier}`
                              );
                              const isDestructivelyUpdateable =
                                !aa || aa.isDestructivelyUpdateable.result;
                              return !isEditing || isDestructivelyUpdateable;
                            }}
                            hasError={(index) => !!errors.children?.[index]}
                            emptyStateText="Add fields to this fieldset"
                          />
                        </Field>
                      );
                    }}
                    name="selectedFieldset"
                    control={control}
                  />
                </Card.Body>
              </Card>

              <ValidationMessages
                validationState={fieldsetChildrenValidation}
              />
            </Conditional>
          </Fieldset>
        </div>

        <Conditional condition={type === 'fieldset'}>
          <div ref={rightPanelRef} className="w-1/2 overflow-y-auto p-4">
            {controlledFields.map((field, index) => {
              const actionAvailabilityForChild = actionAvailability.find(
                (x) =>
                  x.fieldPath ===
                  `${fieldsetIdentifier}.${field.identification.identifier}`
              );

              const isDestructivelyUpdateable =
                actionAvailabilityForChild?.isDestructivelyUpdateable || {
                  result: true,
                  reasons: [],
                };

              const isPersisted = !!defaultValues?.children?.find(
                (c) =>
                  c.identification.identifier ===
                  field.identification.identifier
              );

              return (
                <Fieldset
                  key={field.id}
                  className={clstx(selectedFieldset !== field.id && 'hidden')}
                >
                  {!isDestructivelyUpdateable.result && (
                    <Alert colour="yellow" className="mb-6">
                      <p>Cannot delete or fully edit this field:</p>
                      <ul className="m-0">
                        {isDestructivelyUpdateable.reasons.map((r, i) => (
                          <li key={`is-destructively-updateable-reason-${i}`}>
                            {r}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  <CreateEditCustomFieldFieldset
                    allCurrentIdentifiers={fieldsetChildrenIdentifiers}
                    isDestructivelyUpdateable={isDestructivelyUpdateable}
                    fieldPath={`children.${index}`}
                    isPersisted={isPersisted}
                    onOpenNestedFieldEditor={onOpenNestedFieldEditor}
                    isNestedFieldEditorOpen={
                      nestedFieldEditorState?.parentFieldIndex === index
                    }
                    actionAvailability={actionAvailability}
                    fieldsetIdentifier={fieldsetIdentifier || ''}
                  />
                </Fieldset>
              );
            })}

            {typeof selectedFieldset !== 'string' &&
              controlledFields.length > 0 && (
                <EmptyState
                  className="mt-8"
                  iconName="cursor-box.3"
                  text="Select a field to edit"
                />
              )}
          </div>
        </Conditional>
      </div>

      <Sidebar.Footer>
        <Button
          className={clstx(type === 'fieldset' ? 'w-1/2' : 'w-full')}
          type="submit"
          disabled={!isDirty}
          isProcessing={isProcessing}
        >
          {isEditing ? 'Update' : 'Create'} custom{' '}
          {type !== 'fieldset' ? 'field' : 'fieldset'}
        </Button>
      </Sidebar.Footer>

      {nestedFieldEditorState && (
        <NestedFieldEditorPanel
          nestedFieldEditorState={nestedFieldEditorState}
          controlledFields={controlledFields}
          fieldsetIdentifier={fieldsetIdentifier}
          actionAvailability={actionAvailability}
          defaultValues={defaultValues}
          onCloseNestedFieldEditor={onCloseNestedFieldEditor}
        />
      )}
    </Form>
  );
};

type TNestedFieldEditorPanelProps = {
  nestedFieldEditorState: NonNullable<NestedFieldEditorState>;
  controlledFields: Array<FormValues['children'][number] & { id: string }>;
  fieldsetIdentifier: string | undefined;
  actionAvailability: CustomFieldDefinitionActionAvailability[];
  defaultValues: FormValues | undefined;
  onCloseNestedFieldEditor: () => void;
};

const NestedFieldEditorPanel: FC<TNestedFieldEditorPanelProps> = (props) => {
  const {
    nestedFieldEditorState,
    controlledFields,
    fieldsetIdentifier,
    actionAvailability,
    defaultValues,
    onCloseNestedFieldEditor,
  } = props;

  const { parentFieldIndex, nestedFieldIndex } = nestedFieldEditorState;

  // Parent must be a fieldset (only fieldsets have nested children to edit)
  const parentField = controlledFields[
    parentFieldIndex
  ] as NestedFieldsetFormValue;
  const nestedFieldNamePrefix =
    `children.${parentFieldIndex}.children.${nestedFieldIndex}` as PathToFieldsetChild;

  const parentFieldChildren = parentField.children;
  const nestedChildIdentifiers = parentFieldChildren.map(
    (c) => c.identification?.identifier || ''
  );

  const nestedField = parentFieldChildren[nestedFieldIndex];
  const actionAvailabilityForNestedField = actionAvailability.find(
    (x) =>
      x.fieldPath ===
      `${fieldsetIdentifier}.${parentField.identification.identifier}.${nestedField.identification?.identifier}`
  );

  const nestedIsDestructivelyUpdateable =
    actionAvailabilityForNestedField?.isDestructivelyUpdateable || {
      result: true,
      reasons: [],
    };

  // Parent default must be a fieldset if it exists
  const parentFieldDefault = defaultValues?.children?.[parentFieldIndex] as
    | NestedFieldsetFormValue
    | undefined;
  const nestedFieldDefault = parentFieldDefault?.children?.[nestedFieldIndex];
  const nestedIsPersisted = !!nestedFieldDefault?.identification?.identifier;

  return (
    <NestedFieldEditorSidebar
      isOpen={true}
      onClose={onCloseNestedFieldEditor}
      fieldPath={nestedFieldNamePrefix}
      allCurrentIdentifiers={nestedChildIdentifiers}
      isDestructivelyUpdateable={nestedIsDestructivelyUpdateable}
      isPersisted={nestedIsPersisted}
    />
  );
};

export interface IProps {
  isOpen: boolean;
  onClose?: () => void;
  customFieldId?: string;
  customFieldGroupRole: string;
}

const CreateEditCustomFieldSidebar: FC<IProps> = (props) => {
  const { isOpen, onClose, customFieldId, customFieldGroupRole } = props;

  /**
   * state
   */
  const [isLargeSidebar, setIsLargeSidebar] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [nestedFieldEditorState, setNestedFieldEditorState] =
    useState<NestedFieldEditorState>(null);

  const isNestedFieldEditorOpen = nestedFieldEditorState !== null;

  const openNestedFieldEditor = (parentIndex: number, nestedIndex: number) => {
    setNestedFieldEditorState({
      parentFieldIndex: parentIndex,
      nestedFieldIndex: nestedIndex,
    });
  };

  const closeNestedFieldEditor = () => {
    setNestedFieldEditorState(null);
  };

  /**
   * queries
   */
  const queryClient = useQueryClient();
  const { customFieldGroup, identifiers } = useGetCustomFieldGroup(
    customFieldGroupRole,
    customFieldId
  );

  // Fetch actionAvailability separately for performance (only when editing an existing field)
  const { fieldset: customField } = useGetFieldset(customFieldId);

  const fieldset =
    customField?.type === 'fieldset'
      ? new FieldsetCustomFieldDefinition(customField.schema)
      : undefined;

  /**
   * mutations
   */
  const { createCustomField, isMutating: isCreatingCustomField } =
    useCreateCustomField();
  const { updateCustomField, isMutating: isUpdatingCustomField } =
    useUpdateCustomField();

  /**
   * callbacks
   */
  const handleSubmit = async (data: FormValues, originalData?: FormValues) => {
    if (!customFieldGroup) {
      return;
    }

    if (customField) {
      // Edit
      await updateCustomField(customField.id, data, originalData);
    } else {
      await createCustomField(customFieldGroup.id, data);
    }

    void queryClient.invalidateQueries({
      queryKey: meKeys.customFieldGroups(),
    });

    // Invalidate the individual custom field query if editing
    if (customField) {
      void queryClient.invalidateQueries({
        queryKey: ['customField', customField.id],
      });
    }

    onClose?.();
  };

  const displayType =
    fieldset?.type && fieldset.type !== 'fieldset' ? 'Field' : 'Fieldset';

  return (
    <Sidebar
      isOpen={isOpen}
      onClose={onClose}
      size={isLargeSidebar ? 'lg' : 'standard'}
      title={`${customFieldId ? 'Edit' : 'Create'} Custom ${displayType}`}
      warnUserOnClose={isFormDirty}
      warnUserContent={{
        body: `The changes to this Custom ${displayType} are not saved and will be lost.`,
        confirm: 'Close',
      }}
      behind={isNestedFieldEditorOpen}
    >
      {!customFieldId || fieldset ? (
        <CreateEditCustomFieldSidebarContent
          onSubmit={handleSubmit}
          onFieldsetSelectionChange={setIsLargeSidebar}
          setIsFormDirty={setIsFormDirty}
          allCurrentIdentifiers={identifiers.all}
          fieldset={fieldset}
          isProcessing={isCreatingCustomField || isUpdatingCustomField}
          actionAvailability={customField?.actionAvailability || []}
          nestedFieldEditorState={nestedFieldEditorState}
          onOpenNestedFieldEditor={openNestedFieldEditor}
          onCloseNestedFieldEditor={closeNestedFieldEditor}
        />
      ) : (
        <div>Loading...</div>
      )}
    </Sidebar>
  );
};

export default CreateEditCustomFieldSidebar;
